import { useSelectedAgentModel } from "@ext/agent/components/hooks/useSelectedAgentModel";
import { ChatReasoningEffortSlider } from "@ext/agent/components/panel/ChatReasoningEffortSlider";
import { useAgentChatIsOpen } from "@ext/agent/components/store/AgentChatIsOpenStore";
import { formatReasoningEffort } from "@ext/agent/components/utils/formatReasoningEffort";
import type { AiAgentModel } from "@ext/agent/llm";
import t from "@ext/localization/locale/translate";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTriggerButton,
} from "@ui-kit/Dropdown";
import { Icon, type IconCode } from "@ui-kit/Icon";
import { TextOverflowTooltip, Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useState } from "react";
import { getCurrentLanguage } from "../../../localization/locale/translate";

type Props = {
	models: AiAgentModel[];
	onModelChange: (model: AiAgentModel) => void;
	disabled?: boolean;
	isModelsLoading?: boolean;
};

const getModelLabel = (model: AiAgentModel): string => model.title || model.name;

const ChatModelBadges = ({ badges }: { badges?: AiAgentModel["badges"] }) => {
	if (!badges?.length) return null;

	const language = getCurrentLanguage();

	return (
		<span className="flex shrink-0 items-center gap-1">
			{badges.map((badge) => (
				<Tooltip key={`${badge.icon}-${badge.tooltip.en}`}>
					<TooltipTrigger asChild>
						<span className="inline-flex text-muted">
							<Icon className="size-3" icon={badge.icon as IconCode} />
						</span>
					</TooltipTrigger>
					<TooltipContent>{language === "en" ? badge.tooltip.en : badge.tooltip.ru}</TooltipContent>
				</Tooltip>
			))}
		</span>
	);
};

const ChatModelMenu = ({ models, onModelChange, disabled, isModelsLoading }: Props) => {
	const selection = useSelectedAgentModel();
	const isPanelOpen = useAgentChatIsOpen();
	const [open, setOpen] = useState(false);
	if (open && !isPanelOpen) setOpen(false);

	if (isModelsLoading)
		return (
			<div
				aria-busy="true"
				className="flex h-7 w-full gap-1 items-center justify-between px-1.5 text-xs text-muted"
			>
				<Icon
					aria-hidden="true"
					className="size-3 shrink-0 animate-spin motion-reduce:animate-none"
					icon="loader-circle"
				/>
				<span>{t("agent.input.models-loading")}</span>
			</div>
		);

	if (!selection) return null;

	const selectedModel = selection?.model;
	const reasoningEffort = selection?.reasoningEffort;

	const handleValueChange = (id: string) => {
		const model = models.find((m) => m.id === id);
		if (model) onModelChange(model);
	};

	return (
		<DropdownMenu modal={false} onOpenChange={setOpen} open={open}>
			<DropdownMenuTriggerButton
				className="h-7 w-full min-w-0 px-1.5 font-normal"
				containerClassName="gap-1"
				disabled={disabled}
				endIcon="chevron-down"
				iconClassName="text-muted"
				size="xs"
				variant="ghost"
			>
				{selectedModel ? (
					<TextOverflowTooltip className="min-w-0 truncate">
						{getModelLabel(selectedModel)}
					</TextOverflowTooltip>
				) : null}
				{reasoningEffort && (
					<span className="shrink-0 font-normal text-muted">{formatReasoningEffort(reasoningEffort)}</span>
				)}
			</DropdownMenuTriggerButton>
			<DropdownMenuContent align="start" side="top">
				<DropdownMenuRadioGroup
					indicatorIconPosition="start"
					onValueChange={handleValueChange}
					value={selectedModel?.id}
				>
					{models.map((model) => (
						<DropdownMenuRadioItem key={model.id} value={model.id}>
							<span className="flex min-w-0 items-center gap-1 justify-between">
								<TextOverflowTooltip className="truncate whitespace-nowrap">
									{getModelLabel(model)}
								</TextOverflowTooltip>
								<ChatModelBadges badges={model.badges} />
							</span>
						</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
				<ChatReasoningEffortSlider />
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export { ChatModelMenu };
